import { z } from "zod";
import { StandardResponseSchema, pageSchema } from "@/helpers/types";

// Zod Schemas
export const ExperimentInsightSchema = z.object({
  tag_rk: z.string(),
});

export const ExperimentEntitySchema = z.object({
  entity_rk: z.string(),
  entity_type: z.string(),
  entity_name: z.string(),
  insights: z.array(ExperimentInsightSchema).nullish(),
});

export const ExperimentMetricSchema = z.object({
  metric_type: z.string(),
});

// Experiment Item (for list view)
export const ExperimentItemSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  experiment_status: z.string(),
  created_by: z.number(),
  experiment_start: z.string(),
  experiment_end: z.string(),
  created_on: z.string(),
  entities: z.array(ExperimentEntitySchema),
});
export type ExperimentItem = z.infer<typeof ExperimentItemSchema>;

// Get All Experiments Response (paginated)
export const GetAllExperimentsResponseSchema = pageSchema(ExperimentItemSchema);
export type GetAllExperimentsResponse = z.infer<typeof GetAllExperimentsResponseSchema>;

export type ExperimentEntity = z.infer<typeof ExperimentEntitySchema>;
export type ExperimentMetric = z.infer<typeof ExperimentMetricSchema>;

// Get All Experiments Params
export interface GetAllExperimentsParams {
  page?: number;
  size?: number;
  sort_key?: string;
  sort_order?: string;
  users?: number[];
  status?: string[];
  entities?: string[];
}

// Experiment Detail (for single view)
export const ExperimentDetailSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  hypothesis: z.string(),
  experiment_start: z.string(),
  experiment_end: z.string(),
  instructions: z.string(),
  experiment_status: z.string(),
  created_by: z.number(),
  created_on: z.string(),
  effort_level: z.string().nullable(),
  entities: z.array(ExperimentEntitySchema),
});
export type ExperimentDetail = z.infer<typeof ExperimentDetailSchema>;

// Experiment Summary Response
export const ExperimentSummaryResponseSchema = z.object({
  total_experiments: z.number(),
  inprogress_experiments: z.number(),
  success_experiments: z.number(),
});
export type ExperimentSummaryResponse = z.infer<typeof ExperimentSummaryResponseSchema>;

// Create Experiment Request
export const CreateExperimentRequestSchema = z.object({
  name: z.string(),
  hypothesis: z.string(),
  experiment_start: z.string(),
  experiment_end: z.string(),
  instructions: z.string(),
  effort_level: z.string(),
  entities: z.array(ExperimentEntitySchema),
  metrics: z.array(ExperimentMetricSchema),
});
export type CreateExperimentRequest = z.infer<typeof CreateExperimentRequestSchema>;

// Update Experiment Status Request
export const UpdateExperimentStatusRequestSchema = z.object({
  experiment_status: z.string(),
});
export type UpdateExperimentStatusRequest = z.infer<typeof UpdateExperimentStatusRequestSchema>;

// Schema Metadata Item
export const SchemaMetadataItemSchema = z.object({
  rk: z.string(),
  name: z.string(),
});
export type SchemaMetadataItem = z.infer<typeof SchemaMetadataItemSchema>;

// Schema Metadata Response
export const SchemaMetadataResponseSchema = z.array(SchemaMetadataItemSchema);
export type SchemaMetadataResponse = z.infer<typeof SchemaMetadataResponseSchema>;

// Re-export StandardResponseSchema as StandardApiResponseSchema for backward compatibility
export { StandardResponseSchema as StandardApiResponseSchema } from "@/helpers/types";
export type StandardApiResponse = z.infer<typeof StandardResponseSchema>;
